import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { useState } from 'react';

import {
  AutocompleteMultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import {
  TargetingSupplyType,
  type NamedTargetingEntity,
  type TargetingFormValue,
} from '../../../model';
import {
  ManageAppsDialog,
  ManageDomainsDialog,
} from '../../../../campaigns/supply/components/TailoredSupplyEditorPage/ManageSupplySelectionDialog';
import { ManageTailoredSuppliesDialog } from '../../../../campaigns/supply/components/TailoredSupplyEditorPage/ManageTailoredSuppliesDialog';
import type { TargetingFormUpdate } from '../TargetingForm.types';
import {
  moveSelectionOnInclusionToggle,
  toSupplyApps,
  toSupplyDomains,
  toTailoredSupplies,
} from '../TargetingSupplyFields.helpers';
import { InclusionToggle } from '../InclusionToggle';
import { ManagedSelectionSection } from '../ManagedSelectionSection';
import {
  SelectedAppsTable,
  SelectedDomainsTable,
  SelectedTailoredSupplyTable,
} from '../SelectedSupplyTables';
import { TargetingSection } from '../TargetingSection';

enum LineItemSupplyDialog {
  Apps = 'APPS',
  Domains = 'DOMAINS',
  TailoredSupply = 'TAILORED_SUPPLY',
}

export function TargetingSupplyFields({
  onChange,
  showSupply,
  showSupplyCompanies,
  supplyCompanies,
  value,
}: {
  onChange: (value: TargetingFormValue) => void;
  showSupply: boolean;
  showSupplyCompanies: boolean;
  supplyCompanies: NamedTargetingEntity[];
  value: TargetingFormValue;
}) {
  const [openManageDialog, setOpenManageDialog] = useState<LineItemSupplyDialog | null>(null);
  const { showAppsSection, showDomainsSection } = resolveSupplyTypeSections(
    showSupply,
    value.supplyType,
  );
  const showTailoredSupplySection = showSupply;
  const supplyCompanyIds = value.supplyCompanies.map((company) => company.id);
  const updateField: TargetingFormUpdate = (key, nextValue) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <>
      {showSupplyCompanies ? (
        <TargetingSection title={dashboardT('targeting.supplyCompanies')}>
          <AutocompleteMultiSelect
            label={dashboardT('targeting.supplyCompaniesLabel')}
            placeholder={dashboardT('targeting.chooseCompanies')}
            emptyMessage={dashboardT('targeting.noSupplyCompanies')}
            options={supplyCompanies.map((company) => ({ id: company.id, label: company.name }))}
            value={value.supplyCompanies.map((company) => company.id)}
            maxResults={supplyCompanies.length}
            onValueChange={(ids) => {
              const selectedIds = new Set(ids);
              updateField(
                'supplyCompanies',
                supplyCompanies.filter((company) => selectedIds.has(company.id)),
              );
            }}
          />
        </TargetingSection>
      ) : null}

      {showSupply ? (
        <>
          <TargetingSection title={dashboardT('targeting.supplyType')}>
            <DashboardPageFormField label={dashboardT('targeting.supplyTypeLabel')} required>
              <Select
                value={value.supplyType}
                onValueChange={(value) => {
                  updateField('supplyType', value as TargetingSupplyType);
                }}
              >
                <SelectTrigger aria-label={dashboardT('targeting.supplyTypeLabel')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[TargetingSupplyType.Apps, TargetingSupplyType.Domains].map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === TargetingSupplyType.Apps
                        ? dashboardT('madBuy.common.apps')
                        : dashboardT('madBuy.common.domains')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          </TargetingSection>

          {showAppsSection ? (
            <ManagedSelectionSection
              title={dashboardT('madBuy.common.apps')}
              selectedItems={value.appsIncluded ? value.includedApps : value.excludedApps}
              manageLabel={dashboardT('targeting.manageApps')}
              emptyText={dashboardT('targeting.noAppsSelectedRunAll')}
              onManage={() => {
                setOpenManageDialog(LineItemSupplyDialog.Apps);
              }}
              action={
                <InclusionToggle
                  included={value.appsIncluded}
                  onIncludedChange={(included) => {
                    const next = moveSelectionOnInclusionToggle({
                      included,
                      includedItems: value.includedApps,
                      excludedItems: value.excludedApps,
                    });
                    onChange({
                      ...value,
                      appsIncluded: included,
                      includedApps: next.includedItems,
                      excludedApps: next.excludedItems,
                    });
                  }}
                />
              }
              onClear={() => {
                onChange({ ...value, includedApps: [], excludedApps: [] });
              }}
            >
              <SelectedAppsTable
                items={value.appsIncluded ? value.includedApps : value.excludedApps}
                showCompany
              />
            </ManagedSelectionSection>
          ) : null}

          {showDomainsSection ? (
            <ManagedSelectionSection
              title={dashboardT('madBuy.common.domains')}
              selectedItems={value.domainsIncluded ? value.includedDomains : value.excludedDomains}
              manageLabel={dashboardT('targeting.manageDomains')}
              emptyText={dashboardT('targeting.noDomainsSelectedRunAll')}
              onManage={() => {
                setOpenManageDialog(LineItemSupplyDialog.Domains);
              }}
              action={
                <InclusionToggle
                  included={value.domainsIncluded}
                  onIncludedChange={(included) => {
                    const next = moveSelectionOnInclusionToggle({
                      included,
                      includedItems: value.includedDomains,
                      excludedItems: value.excludedDomains,
                    });
                    onChange({
                      ...value,
                      domainsIncluded: included,
                      includedDomains: next.includedItems,
                      excludedDomains: next.excludedItems,
                    });
                  }}
                />
              }
              onClear={() => {
                onChange({ ...value, includedDomains: [], excludedDomains: [] });
              }}
            >
              <SelectedDomainsTable
                items={value.domainsIncluded ? value.includedDomains : value.excludedDomains}
              />
            </ManagedSelectionSection>
          ) : null}

          {showTailoredSupplySection ? (
            <ManagedSelectionSection
              title={dashboardT('targeting.tailoredSupply')}
              selectedItems={value.includedTailoredSupplies}
              manageLabel={dashboardT('targeting.manageTailoredSupply')}
              emptyText={dashboardT('targeting.noTailoredSupplySelectedRunAll')}
              action={<span className="text-sm">{dashboardT('targeting.included')}</span>}
              onManage={() => {
                setOpenManageDialog(LineItemSupplyDialog.TailoredSupply);
              }}
              onClear={() => {
                onChange({
                  ...value,
                  includedTailoredSupplies: [],
                  excludedTailoredSupplies: [],
                  tailoredSupplyExcludedApps: [],
                  tailoredSupplyExcludedDomains: [],
                });
              }}
            >
              <SelectedTailoredSupplyTable
                items={value.includedTailoredSupplies}
                supplyType={value.supplyType}
                excludedApps={value.tailoredSupplyExcludedApps}
                excludedDomains={value.tailoredSupplyExcludedDomains}
                onExcludedAppsChange={(items) => {
                  updateField('tailoredSupplyExcludedApps', items);
                }}
                onExcludedDomainsChange={(items) => {
                  updateField('tailoredSupplyExcludedDomains', items);
                }}
              />
            </ManagedSelectionSection>
          ) : null}

          {openManageDialog === LineItemSupplyDialog.Apps ? (
            <ManageAppsDialog
              open
              selectedItems={toSupplyApps(
                value.appsIncluded ? value.includedApps : value.excludedApps,
              )}
              companyIds={supplyCompanyIds}
              onOpenChange={(open) => {
                setOpenManageDialog(open ? LineItemSupplyDialog.Apps : null);
              }}
              onConfirm={(apps) => {
                if (value.appsIncluded) {
                  updateField('includedApps', apps);
                } else {
                  updateField('excludedApps', apps);
                }
              }}
            />
          ) : null}
          {openManageDialog === LineItemSupplyDialog.Domains ? (
            <ManageDomainsDialog
              open
              selectedItems={toSupplyDomains(
                value.domainsIncluded ? value.includedDomains : value.excludedDomains,
              )}
              companyIds={supplyCompanyIds}
              onOpenChange={(open) => {
                setOpenManageDialog(open ? LineItemSupplyDialog.Domains : null);
              }}
              onConfirm={(domains) => {
                if (value.domainsIncluded) {
                  updateField('includedDomains', domains);
                } else {
                  updateField('excludedDomains', domains);
                }
              }}
            />
          ) : null}
          {openManageDialog === LineItemSupplyDialog.TailoredSupply ? (
            <ManageTailoredSuppliesDialog
              open
              selectedItems={toTailoredSupplies(value.includedTailoredSupplies)}
              onOpenChange={(open) => {
                setOpenManageDialog(open ? LineItemSupplyDialog.TailoredSupply : null);
              }}
              onConfirm={(tailoredSupplies) => {
                onChange({
                  ...value,
                  tailoredSuppliesIncluded: true,
                  includedTailoredSupplies: tailoredSupplies,
                  excludedTailoredSupplies: [],
                });
              }}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
}

function resolveSupplyTypeSections(showSupply: boolean, supplyType: TargetingSupplyType) {
  if (!showSupply) return { showAppsSection: false, showDomainsSection: false };
  switch (supplyType) {
    case TargetingSupplyType.Apps:
      return { showAppsSection: true, showDomainsSection: false };
    case TargetingSupplyType.Domains:
      return { showAppsSection: false, showDomainsSection: true };
    case TargetingSupplyType.All:
      return { showAppsSection: false, showDomainsSection: false };
  }
}
